import React, { useEffect, useReducer, useState } from 'react';
import Box from '@mui/material/Box';
import {
  useSigninCheck, useUser, useFirestore,
} from 'reactfire';
import {
  doc, getDoc,
  addDoc,
  collection,
} from 'firebase/firestore';
import useGlobal from 'global-state/store';
import { MenuProvider } from 'menu-actions/MenuContext';
import {
  BrowserRouter, Routes, Route, useNavigate, useLocation,
} from 'react-router-dom';
import StoragePDFViewerPage from 'components/StoragePDFViewerPage';
import MyAccount from 'components/account/MyAccount';
import cgu from 'staticFiles/CGU.pdf';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from 'react-i18next';
import GlobalSnackbar from 'components/GlobalSnackbar';
import { Cookies, Contact } from 'labox-ws-commons';
import { getAnalytics } from 'firebase/analytics';
import RemoteConfigStatus from 'components/RemoteConfigStatus';
import LoadingPage from 'components/LoadingPage';
import Admin from 'components/admin/Admin';
import SignInPage from './SignInPage';
import TopButtons from './TopButtons';
import Footer from './Footer';
import PDFView from './PDFView';
import TruckTanks from './admin/managingTruckTanks/Tanks';
import CellarTanks from './admin/managingCellarTanks/Tanks';
import Certificates from './admin/managinCertificates/Certificates';
import CertificateCreation from './admin/managinCertificates/creation/CertificateCreation';
import CertificateUpdateInfo from './admin/managinCertificates/updateInfo/CertificateUpdateInfo';
import ScanCertificatePdf from './admin/certificatePdfScanner/ScanCertificatePdf';
import ScannedCertificatesList from './admin/certificatePdfScanner/ScannedCertificatesList';
import ScannedCertificateViewer from './admin/certificatePdfScanner/ScannedCertificateViewer';

export default function Home() {
  return <RemoteConfigStatus defaultComponent={<UserSetupMenuAndMainPage />} />;
}

function UserSetupMenuAndMainPage() {
  const { data: user } = useUser();
  const db = useFirestore();
  const [globalState, globalActions] = useGlobal();
  const { status } = useSigninCheck();

  useEffect(() => {
    // eslint-disable-next-line no-promise-executor-return
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    async function getUser(uid) {
      const userRef = doc(db, 'users', uid);
      const firestoreUser = await getDoc(userRef);
      return firestoreUser;
    }
    async function getUserPrivateData(uid) {
      const userRef = doc(db, 'users', uid, 'private', 'data');
      const firestoreUser = await getDoc(userRef);
      return firestoreUser;
    }
    async function fetchMultipleTimesUser(uid) {
      let firestoreUser = await getUser(uid);
      if (!firestoreUser.exists()) {
        await sleep(1000);
        firestoreUser = await getUser(uid);
        if (!firestoreUser.exists()) {
          await await sleep(2000);
          firestoreUser = await getUser(uid);
        }
      }
      return firestoreUser;
    }

    async function setupGlobalStateFromUser() {
      globalActions.setUserStatus('loading');
      await fetchMultipleTimesUser(user.uid);
      const firestoreUserPrivateData = await getUserPrivateData(user.uid);
      const userPrivateData = firestoreUserPrivateData.data();
      globalActions.setActiveOrganization(userPrivateData.activeOrganization);

      if (userPrivateData.activeOrganization === '') {
        globalActions.setDocumentAiEnabled(false);
        return globalActions.setUserStatus('notYetSetup');
      }

      const orgaAppPath = `/organizations/${userPrivateData.activeOrganization}/apps/digitank-gauger`;
      const organizationAppRef = doc(db, orgaAppPath);
      const firestoreorganizationApp = await getDoc(organizationAppRef);
      if (!firestoreorganizationApp.exists()) {
        globalActions.setDocumentAiEnabled(false);
        return globalActions.setUserStatus('notYetSetup');
      }
      const organizationAppData = firestoreorganizationApp.data();
      if (organizationAppData.admins.includes(user.uid)) {
        globalActions.setUserRoles(['admin']);
      }
      globalActions.setDocumentAiEnabled(organizationAppData?.documentAiEnabled === true);
      const orgNames = {};
      await Promise.all(
        organizationAppData.organizationCollaborations.map(async (organizationId) => {
          const organizationRef = doc(db, 'organizations', organizationId);
          const organizationDoc = await getDoc(organizationRef);
          const organizationData = organizationDoc.data();
          orgNames[organizationId] = organizationData.name;
        }),
      );
      globalActions.setClientOrganizations(orgNames);

      return globalActions.setUserStatus('setupDone');
    }

    if (user?.uid !== undefined && globalState.userStatus === 'initial') {
      setupGlobalStateFromUser();
    }
  }, [db, globalActions, globalState.networkState, globalState.userStatus, user?.uid]);

  if (status === 'loading') {
    return (
      <LoadingPage />
    );
  }

  return <MenuProvider app={<MainPage />} />;
}

function MainPage() {
  const [menuActions, dispatchMenuActions] = useReducer(reducerMenuActions, {});
  const [globalState] = useGlobal();
  const [displayAll, setdisplayAll] = useState(false);
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();

  function reducerMenuActions(currentState, dispatch) {
    let finalMenuActions;
    switch (dispatch.type) {
      case 'add':
        finalMenuActions = { ...currentState };
        dispatch.actions.forEach((action) => {
          finalMenuActions[action.name] = action.callback;
        });
        return finalMenuActions;
      case 'set':
        finalMenuActions = { };
        dispatch.actions.forEach((action) => {
          finalMenuActions[action.name] = action.callback;
        });
        return finalMenuActions;
      case 'clear':
        return {};
      default:
        throw new Error();
    }
  }

  const expandIcon = () => (displayAll ? <ExpandLessIcon /> : <ExpandMoreIcon />);

  return (
    <BrowserRouter>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {globalState.networkState === 'offline' && (
        <Alert
          sx={{
            padding: 1, mt: 1, justifyContent: 'center',
          }}
          severity="error"
          onClick={() => setdisplayAll(!displayAll)}
        >
          {t('offline')}
          <Button sx={{ ml: 1, height: 17 }} color="inherit" variant="outlined" size="small">
            {expandIcon()}
          </Button>
          <Collapse
            in={displayAll}
            sx={{
              width: '100%',
            }}
          >
            {t('offline_more')}
          </Collapse>
        </Alert>
        )}
        <Box sx={{
          minHeight: '80vh', display: 'flex', flexDirection: 'column',
        }}
        >
          <GlobalSnackbar />
          <TopButtons menuActions={menuActions} />
          <MainPageContent dispatchMenuActions={dispatchMenuActions} />
        </Box>
        <RoutedFooter />
        <Cookies
          cookieName="agreed-to-cookies-on-digitank-gauger"
          t={t}
          globalActions={globalActions}
        />
      </Box>
    </BrowserRouter>
  );
}

function MainPageContent({ dispatchMenuActions }) {
  const { data: signInCheckResult } = useSigninCheck();
  const navigate = useNavigate();
  const location = useLocation();

  const db = useFirestore();

  useEffect(() => {
    if (!signInCheckResult?.signedIn === true
      && location.pathname !== '/contact'
      && location.pathname !== '/cgu') {
      navigate('/signin');
    }
  }, [location.pathname, navigate, signInCheckResult?.signedIn]);

  const sendEmail = async (values) => {
    const emailContent = `Email: 
      ${values.email}

      Nom et entreprise: 
      ${values.nameAndCompany}

      Message: 
      ${values.message}`;

    await addDoc(
      collection(db, 'mail'),
      {
        to: 'contact@labox-apps.com',
        message: {
          subject: `[Contenants Jaugés, Contact] ${values.nameAndCompany}`,
          text: emailContent,
        },
      },
    );
  };

  if (!signInCheckResult?.signedIn === true) {
    return <NotSignedInRoutes dispatchMenuActions={dispatchMenuActions} sendEmail={sendEmail} />;
  }

  return <AllRoutes dispatchMenuActions={dispatchMenuActions} />;
}

function NotSignedInRoutes({ dispatchMenuActions, sendEmail }) {
  const { t } = useTranslation();
  const [, globalActions] = useGlobal();
  const analytics = getAnalytics();
  const navigate = useNavigate();
  const { data: signInCheckResult } = useSigninCheck();

  return (
    <Routes>
      <Route path="/" element={<Admin />} />
      <Route
        path="pdfViewer/*"
        element={<StoragePDFViewerPage dispatchMenuActions={dispatchMenuActions} />}
      />
      <Route exact path="account" element={<MyAccount />} />
      <Route exact path="signin" element={<SignInPage />} />
      <Route
        path="contact"
        element={(
          <Contact
            t={t}
            navigate={navigate}
            globalActions={globalActions}
            analytics={analytics}
            sendEmail={sendEmail}
            signInCheckResult={signInCheckResult}
          />
        )}
      />
      <Route path="cgu" element={<PDFView path={cgu} />} />
      <Route
        path="*"
        element={(
          <main style={{ padding: '1rem' }}>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <p>There's nothing here!</p>
          </main>
            )}
      />
    </Routes>
  );
}

function RoutedFooter() {
  const location = useLocation();
  const isScanViewer = /^\/certificates\/scanned\/[^/]+$/.test(location.pathname);
  return <Footer variant={isScanViewer ? 'thin' : 'default'} />;
}

function AllRoutes({ sendEmail, dispatchMenuActions }) {
  const { t } = useTranslation();
  const [globalState, globalActions] = useGlobal();
  const analytics = getAnalytics();
  const navigate = useNavigate();
  const { data: signInCheckResult } = useSigninCheck();

  if (globalState.userStatus === 'initial' || globalState.userStatus === 'loading') {
    return <LoadingPage />;
  }

  return (
    <Routes>
      <Route path="/" element={<Admin />} />
      <Route
        path="pdfViewer/*"
        element={<StoragePDFViewerPage dispatchMenuActions={dispatchMenuActions} />}
      />
      <Route exact path="account" element={<MyAccount />} />
      <Route exact path="signin" element={<SignInPage />} />
      <Route
        path="contact"
        element={(
          <Contact
            t={t}
            navigate={navigate}
            globalActions={globalActions}
            analytics={analytics}
            sendEmail={sendEmail}
            signInCheckResult={signInCheckResult}
          />
        )}
      />
      <Route path="cgu" element={<PDFView path={cgu} />} />
      <Route path="truck-tanks" element={<TruckTanks />} />
      <Route path="cellar-tanks" element={<CellarTanks />} />
      <Route path="certificates" element={<Certificates />} />
      <Route path="certificates/create" element={<CertificateCreation />} />
      <Route path="certificates/updateInfo" element={<CertificateUpdateInfo />} />
      <Route path="certificates/scan-pdf" element={<ScanCertificatePdf />} />
      <Route path="certificates/scanned" element={<ScannedCertificatesList />} />
      <Route path="certificates/scanned/:scanId" element={<ScannedCertificateViewer />} />
      <Route
        path="*"
        element={(
          <main style={{ padding: '1rem' }}>
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <p>There's nothing here!</p>
          </main>
            )}
      />
    </Routes>
  );
}
